/* ───────────────────────────────────────────────────────────────
 *  IMPORTS
 * ──────────────────────────────────────────────────────────── */
import React, { useState, useEffect, useCallback } from "react";

import {
  List,
  SimpleForm,
  TextInput,
  TextField,
  DateField,
  Resource,
  Filter,
  Show,
  Datagrid,
  useRecordContext,
} from "react-admin";

import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import ReportProblemOutlinedIcon from "@material-ui/icons/ReportProblemOutlined";

import {
  Tooltip,
  Typography,
  makeStyles,
  Paper,
  Button,
  Snackbar,
} from "@material-ui/core";

import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import CodeIcon from "@material-ui/icons/Code";
import FormatAlignLeftIcon from "@material-ui/icons/FormatAlignLeft";

/* Visor JSON */
import { JsonField } from "react-admin-json-view";

import { useUpdateDom } from "../hooks/useUpdateDom";
import {
  CarbonUploadErrorSummary,
  normalizeCarbonUploadError,
} from "./uploadErrorDetails";
import {
  GtinUploadHistoryResult,
  gtinResultFromHistory,
} from "./ecobalyse/gtinUploadResult";
import {
  TraceabilityHistoryResult,
  parseTraceabilityIssues,
} from "./traceabilityIssues";

/* ───────────────────────────────────────────────────────────────
 *  COMPONENTES AUXILIARES
 * ──────────────────────────────────────────────────────────── */
const StatusIcon = ({ record = {} }) => {
  const { status } = record;
  let icon = null;

  switch (status) {
    case "UPLOADED":
      icon = <CloudUploadIcon htmlColor="orange" />;
      break;
    case "PROCESSING":
      icon = <HourglassEmptyIcon htmlColor="orange" />;
      break;
    case "SUCCESS":
      icon = <CheckCircleOutlineIcon htmlColor="green" />;
      break;
    case "PARTIAL":
      icon = <ReportProblemOutlinedIcon htmlColor="orange" />;
      break;
    case "FAILED":
      icon = <ErrorOutlineIcon htmlColor="red" />;
      break;
    default:
      icon = null;
  }
  return <Tooltip title={status}>{icon}</Tooltip>;
};

const Filters = (props) => (
  <Filter {...props}>
    <TextInput label="Search" source="name" alwaysOn />
  </Filter>
);

const TypeField = ({ source }) => {
  const record = useRecordContext();
  const value = record?.[source];
  return <span>{value === "SIMULATION" ? "CHECK" : value}</span>;
};

/* ───────────────────────────────────────────────────────────────
 *  ESTILOS
 * ──────────────────────────────────────────────────────────── */
const useStyles = makeStyles((theme) => ({
  root: { marginTop: theme.spacing(1), marginBottom: theme.spacing(1) },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(1, 2),
    backgroundColor: theme.palette.grey[100],
    cursor: "pointer",
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    "& > *": { marginLeft: theme.spacing(1) },
  },
  content: {
    padding: theme.spacing(2),
    maxHeight: 500,
    overflow: "auto",
    backgroundColor: theme.palette.grey[50],
  },
  plainText: {
    whiteSpace: "pre-wrap",
    padding: theme.spacing(2),
    maxHeight: 500,
    overflow: "auto",
    fontFamily: "monospace",
    backgroundColor: theme.palette.grey[50],
    border: `1px solid ${theme.palette.divider}`,
  },
}));

/* ───────────────────────────────────────────────────────────────
 *  STACKTRACE FORMATTER
 * ──────────────────────────────────────────────────────────── */
/* ───────────────────────────────────────────────────────────────
 *  SUCCESS FORMATTER
 * ──────────────────────────────────────────────────────────── */
const SuccessFormatter = () => {
  const classes = useStyles();
  const record = useRecordContext();

  const [expanded, setExpanded] = useState(true);
  const [viewMode, setViewMode] = useState("formatted"); // 'formatted' | 'raw'
  const [copyOk, setCopyOk] = useState(false);
  const [data, setData] = useState(null); // obj | string
  const [raw, setRaw] = useState("");

  /* PRE-PROCESA string para convertirlo en JSON válido */
  const preProcess = (str) => {
    // 1. quitar "Success:" al inicio si existe
    let cleaned = str.replace(/^Success:\s*/i, "").trim();

    // 2. si no empieza con "[" lo convertimos en array
    if (!cleaned.startsWith("[")) {
      // 3. unir objetos contiguos "} {", "}\n{" → "},{"
      cleaned = cleaned.replace(/}\s*,?\s*{/g, "},{");
      cleaned = `[${cleaned}]`;
    }
    return cleaned;
  };

  /* parseo inicial */
  useEffect(() => {
    if (!record?.warnings) return;

    const rawStr =
      typeof record.warnings === "string"
        ? record.warnings
        : JSON.stringify(record.warnings, null, 2);

    setRaw(rawStr);

    if (typeof record.warnings === "string") {
      let parsed = null;
      try {
        parsed = JSON.parse(record.warnings); // ¿ya es JSON?
      } catch {
        try {
          parsed = JSON.parse(preProcess(record.warnings)); // plan B
        } catch {
          /* sigue fallando → se mostrará como texto */
        }
      }
      setData(parsed || record.warnings);
      if (!parsed) setViewMode("raw");
    } else {
      setData(record.warnings);
    }
  }, [record]);

  /* copiar al portapapeles */
  const handleCopy = useCallback(() => {
    if (!data) return;
    const txt =
      typeof data === "object" ? JSON.stringify(data, null, 2) : String(data);
    navigator.clipboard.writeText(txt).then(() => {
      setCopyOk(true);
      setTimeout(() => setCopyOk(false), 2000);
    });
  }, [data]);

  // Solo mostrar cuando hay warnings y el estado es SUCCESS o PARTIAL.
  // Las cargas GTIN con resultado estructurado muestran sus avisos agrupados en su propia vista.
  // Traceability también (TraceabilityHistoryResult); "{}" es un array vacío guardado por el Check.
  const traceabilityHandled =
    record?.tab === "TRACEABILITY" &&
    (String(record?.warnings).trim() === "{}" ||
      parseTraceabilityIssues(record?.warnings).length > 0);
  if (
    !record?.warnings ||
    record?.warnings === "" ||
    !["SUCCESS", "PARTIAL"].includes(record?.status) ||
    gtinResultFromHistory(record) ||
    traceabilityHandled
  )
    return null;

  return (
    <Paper className={classes.root} variant="outlined">
      {/* CABECERA */}
      <div className={classes.header}>
        <div onClick={() => setExpanded(!expanded)}>
          <Typography
            variant="subtitle2"
            style={{ display: "flex", alignItems: "center" }}
          >
            <ReportProblemOutlinedIcon
              fontSize="small"
              style={{ marginRight: 8, color: "orange" }}
            />
            {expanded ? "Warnings" : "Expand warnings"}
          </Typography>
        </div>

        <div className={classes.headerActions}>
          {expanded && (
            <>
              <Button
                size="small"
                variant="outlined"
                startIcon={<FileCopyIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCopy();
                }}
              >
                Copy
              </Button>

              <Button
                size="small"
                variant="outlined"
                startIcon={
                  viewMode === "formatted" ? (
                    <CodeIcon />
                  ) : (
                    <FormatAlignLeftIcon />
                  )
                }
                onClick={(e) => {
                  e.stopPropagation();
                  setViewMode(viewMode === "formatted" ? "raw" : "formatted");
                }}
              >
                {viewMode === "formatted" ? "View as text" : "View formatted"}
              </Button>
            </>
          )}
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </div>
      </div>

      {/* CONTENIDO */}
      {expanded &&
        (viewMode === "formatted" ? (
          <div className={classes.content}>
            {typeof data === "object" ? (
              <JsonField
                record={{ warnings: data }} // wrapper ficticio
                source="warnings"
                jsonString={false}
                reactJsonOptions={{
                  name: null,
                  collapsed: 1,
                  displayDataTypes: false,
                  enableClipboard: false,
                  collapseStringsAfterLength: 100,
                }}
              />
            ) : (
              <pre className={classes.plainText}>{data}</pre>
            )}
          </div>
        ) : (
          <pre className={classes.plainText}>{raw}</pre>
        ))}

      <Snackbar
        open={copyOk}
        message="Copied to clipboard"
        autoHideDuration={2000}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Paper>
  );
};

const StacktraceFormatter = () => {
  const classes = useStyles();
  const record = useRecordContext();

  const [expanded, setExpanded] = useState(true);
  const [viewMode, setViewMode] = useState("formatted"); // 'formatted' | 'raw'
  const [copyOk, setCopyOk] = useState(false);
  const [data, setData] = useState(null); // obj | string
  const [raw, setRaw] = useState("");
  const carbonError = normalizeCarbonUploadError(data);

  /* PRE-PROCESA string para convertirlo en JSON válido */
  const preProcess = (str) => {
    // 1. quitar "Error:" al inicio
    let cleaned = str.replace(/^Error:\s*/i, "").trim();

    // 2. si no empieza con "[" lo convertimos en array
    if (!cleaned.startsWith("[")) {
      // 3. unir objetos contiguos "} {", "}\n{" → "},{"
      cleaned = cleaned.replace(/}\s*,?\s*{/g, "},{");
      cleaned = `[${cleaned}]`;
    }
    return cleaned;
  };

  /* parseo inicial */
  useEffect(() => {
    if (!record?.stacktrace) return;

    const rawStr =
      typeof record.stacktrace === "string"
        ? record.stacktrace
        : JSON.stringify(record.stacktrace, null, 2);

    setRaw(rawStr);

    if (typeof record.stacktrace === "string") {
      let parsed = null;
      try {
        parsed = JSON.parse(record.stacktrace); // ¿ya es JSON?
      } catch {
        try {
          parsed = JSON.parse(preProcess(record.stacktrace)); // plan B
        } catch {
          /* sigue fallando → se mostrará como texto */
        }
      }
      setData(parsed || record.stacktrace);
      if (!parsed) setViewMode("raw");
    } else {
      setData(record.stacktrace);
    }
  }, [record]);

  /* copiar al portapapeles */
  const handleCopy = useCallback(() => {
    if (!data) return;
    const txt =
      typeof data === "object" ? JSON.stringify(data, null, 2) : String(data);
    navigator.clipboard.writeText(txt).then(() => {
      setCopyOk(true);
      setTimeout(() => setCopyOk(false), 2000);
    });
  }, [data]);

  // Si no hay datos de error o el estado no es FAILED, no mostramos nada.
  // Las cargas GTIN y Traceability con errores reconocibles tienen su propia vista.
  if (
    !record?.stacktrace ||
    record?.status !== "FAILED" ||
    gtinResultFromHistory(record) ||
    (record?.tab === "TRACEABILITY" &&
      parseTraceabilityIssues(record.stacktrace).length > 0)
  )
    return null;

  return (
    <Paper className={classes.root} variant="outlined">
      {/* CABECERA */}
      <div className={classes.header}>
        <div onClick={() => setExpanded(!expanded)}>
          <Typography
            variant="subtitle2"
            style={{ display: "flex", alignItems: "center" }}
          >
            <ErrorOutlineIcon fontSize="small" style={{ marginRight: 8 }} />
            {expanded ? "Error details" : "Expand error details"}
          </Typography>
        </div>

        <div className={classes.headerActions}>
          {expanded && (
            <>
              <Button
                size="small"
                variant="outlined"
                startIcon={<FileCopyIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCopy();
                }}
              >
                Copy
              </Button>

              <Button
                size="small"
                variant="outlined"
                startIcon={
                  viewMode === "formatted" ? (
                    <CodeIcon />
                  ) : (
                    <FormatAlignLeftIcon />
                  )
                }
                onClick={(e) => {
                  e.stopPropagation();
                  setViewMode(viewMode === "formatted" ? "raw" : "formatted");
                }}
              >
                {viewMode === "formatted" ? "View as text" : "View formatted"}
              </Button>
            </>
          )}
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </div>
      </div>

      {/* CONTENIDO */}
      {expanded &&
        (viewMode === "formatted" ? (
          <div className={classes.content}>
            {typeof data === "object" ? (
              carbonError ? (
                <CarbonUploadErrorSummary error={carbonError} />
              ) : (
                <JsonField
                  record={{ stack: data }} // wrapper ficticio
                  source="stack"
                  jsonString={false}
                  reactJsonOptions={{
                    name: null,
                    collapsed: 1,
                    displayDataTypes: false,
                    enableClipboard: false,
                    collapseStringsAfterLength: 100,
                  }}
                />
              )
            ) : (
              <pre className={classes.plainText}>{data}</pre>
            )}
          </div>
        ) : (
          <pre className={classes.plainText}>{raw}</pre>
        ))}

      <Snackbar
        open={copyOk}
        message="Copied to clipboard"
        autoHideDuration={2000}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Paper>
  );
};

/* ───────────────────────────────────────────────────────────────
 *  LISTA Y SHOW
 * ──────────────────────────────────────────────────────────── */
export const UploadStatusList = (props) => {
  useUpdateDom();
  return (
    <List
      {...props}
      filters={Filters(props)}
      sort={{ field: "lastStatusDate", order: "DESC" }}
    >
      <div style={{ marginTop: 15 }}>
        <Datagrid rowClick="show">
          <TextField source="filename" />
          <TextField source="tab" label="Upload" />
          <TypeField source="type" />
          <TextField source="username" label="Account" />
          <TextField source="projectname" label="Project name" />
          <StatusIcon label="Status" />
          <DateField showTime source="uploadDate" label="Upload date" />
          <DateField
            showTime
            source="lastStatusDate"
            label="Last status date"
          />
        </Datagrid>
      </div>
    </List>
  );
};

const GtinHistoryDetails = () => (
  <GtinUploadHistoryResult record={useRecordContext()} />
);

const TraceabilityHistoryDetails = () => (
  <TraceabilityHistoryResult record={useRecordContext()} />
);

const ShowUploadStatus = (props) => {
  useUpdateDom();
  return (
    <Show {...props}>
      <SimpleForm>
        <TextField source="filename" />
        <TextField source="tab" label="Upload" />
        <TypeField source="type" />
        <TextField source="username" label="Account" />
        <TextField source="projectname" label="Project name" />
        <TextField source="status" />
        <StatusIcon label="Status" />
        {/* GTIN upload result: summary, filterable errors and download */}
        <GtinHistoryDetails />
        {/* Traceability upload result: errors and warnings grouped by cause */}
        <TraceabilityHistoryDetails />
        {/* Error details formatter */}
        <StacktraceFormatter />
        {/* Success details formatter */}
        <SuccessFormatter />
        <DateField showTime source="uploadDate" label="Upload date" />
        <DateField showTime source="lastStatusDate" label="Last status date" />
      </SimpleForm>
    </Show>
  );
};

/* ───────────────────────────────────────────────────────────────
 *  RESOURCE
 * ──────────────────────────────────────────────────────────── */
const uploadStatusResource = () => (
  <Resource
    name="uploadHistory"
    options={{ label: "File uploads", menuGroup: "Upload logs" }}
    list={UploadStatusList}
    show={ShowUploadStatus}
  />
);

export default uploadStatusResource;
